import { useQuery, useZero } from "@rocicorp/zero/react";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useEffectEvent } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useCurrentUserGrants } from "../hooks/useCurrentUserGrants";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { useWorkflowZipImport } from "../hooks/useWorkflowZipImport";
import { fetchGalleryWorkflowArtifact } from "../lib/galleryImport";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { navigate, routes } from "../lib/urls";
import { EmptyState } from "./EmptyState";
import { Spinner } from "./Spinner";
import { useToast } from "./Toast";

export function GalleryImportRoute({
  workflowId,
  commitId,
  sha256,
}: {
  workflowId: string;
  commitId: string;
  sha256: string;
}) {
  const z = useZero();
  const personalWorkEnabled = usePersonalWorkEnabled();
  const [personalSpace] = useQuery(
    personalWorkEnabled ? queries.personalSpace() : undefined
  );
  const [preferences, preferencesResult] = useQuery(queries.userPreferences());
  const [spaces, spacesResult] = useSpaces({ settingsView: false });
  const [grants, grantsResult] = useCurrentUserGrants();
  const sharedSpacesSettled =
    preferencesResult.type === "complete" &&
    spacesResult.type === "complete" &&
    grantsResult.type === "complete";
  const memberSpaces = spaces.filter(isSpaceMember);
  const writableSpaceIds = getWritableScopeIds(
    grants,
    memberSpaces.map((space) => space.id),
    "space:write"
  );
  const writableSpaces = memberSpaces.filter((space) =>
    writableSpaceIds.has(space.id)
  );
  const spaceId = personalWorkEnabled
    ? (personalSpace?.id ?? null)
    : sharedSpacesSettled
      ? (writableSpaces.find((space) => space.id === preferences?.defaultSpace)
          ?.id ??
        writableSpaces[0]?.id ??
        null)
      : null;
  const { importError, importWorkflowZip } = useWorkflowZipImport(spaceId);
  const { showToast } = useToast();
  const importWorkflowZipEvent = useEffectEvent(importWorkflowZip);
  const handleError = useCallback(
    (error: Error) => {
      Sentry.captureException(error, {
        tags: { source: "gallery-import" },
        extra: {
          galleryWorkflowId: workflowId,
          galleryCommitId: commitId,
        },
      });
      showToast({
        icon: "circle-off",
        message: error.message,
        buttonLabel: "Dismiss",
        variant: "error",
      });
      navigate(
        personalWorkEnabled ? routes.personal : routes.recents,
        undefined,
        { replace: true }
      );
    },
    [commitId, personalWorkEnabled, showToast, workflowId]
  );

  useEffect(() => {
    if (!spaceId) return;
    void fetchGalleryWorkflowArtifact({
      workflowId,
      commitId,
      sha256,
    })
      .then(importWorkflowZipEvent)
      .catch((error: unknown) => {
        handleError(
          error instanceof Error
            ? error
            : new Error(
                typeof error === "string" ? error : "Couldn’t import workflow"
              )
        );
        return false;
      })
      .then((imported) => {
        if (imported) {
          z.mutate(
            sharedMutators.recordGalleryWorkflowImported({
              galleryWorkflowId: workflowId,
              galleryCommitId: commitId,
            })
          );
        }
      });
  }, [commitId, handleError, sha256, spaceId, workflowId, z]);

  useEffect(() => {
    if (importError) handleError(new Error(importError));
  }, [handleError, importError]);

  if (!personalWorkEnabled && sharedSpacesSettled && spaceId == null) {
    return (
      <EmptyState
        iconName="hash"
        title="No space available"
        description="You need write access to a shared space before you can import this workflow."
      />
    );
  }

  return (
    <EmptyState
      icon={<Spinner size={24} />}
      title="Importing workflow…"
      description="This should only take a moment."
    />
  );
}
